/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { css } from "@emotion/css";
import { ArrowLeftIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, fontSize, fontWeight, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { ActionHandlerCategory, type DeploymentTargetResource, type EnvironmentResource, Permission, type ProjectResource, type ProjectTemplateDetailsResource, type PublishedAndSharedProjectTemplateSummary, } from "@octopusdeploy/octopus-server-client";
import type { Url } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Action, useAnalyticActionDispatch, useAnalyticGuidedSetupDispatch, useAnalyticTrackedActionDispatch, useCorrelationId } from "~/analytics/Analytics";
import { ProjectStatusBar } from "~/areas/projects/components/ProjectStatus/ProjectStatusBar";
import { projectStatusActions } from "~/areas/projects/components/ProjectStatus/actions";
import type { ProjectStatus } from "~/areas/projects/components/ProjectStatus/useProjectStatus";
import ProjectTemplateSelectionPage from "~/areas/projects/components/ProjectTemplates/ProjectTemplateSelectionPage";
import { createProject, type ProjectCreatedOption, type CreateProjectInput, ProjectInfrastructure, ProjectInfrastructureSubclass } from "~/areas/projects/components/Projects/AddProject";
import { repository } from "~/clientInstance";
import ConnectArgoCDInstance from "~/components/ProjectBasedActivation/ConnectArgoCDInstance";
import useLocalStorage from "~/hooks/useLocalStorage";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import { type DoBusyTask, useDoBusyTaskEffect } from "../DataBaseComponent";
import InternalLink from "../Navigation/InternalLink";
import { useSpaceAwareNavigation } from "../Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { isAllowed } from "../PermissionCheck/PermissionCheck";
import { CreateEnvironmentsPage } from "./CreateEnvironmentsDialog";
import CreateNewKubernetesCluster from "./CreateNewKubernetesCluster";
import CreateNewProject, { type ProjectTemplateConfig } from "./CreateNewProject";
import ProjectTemplateVersionSelectionPage from "./ProjectTemplateVersionSelectionPage";
/**
 * Controls navigation behavior when the wizard closes.
 * - `default` - redirect to projects page on cancel, project page on success
 * - `skipCancel` - no redirect on cancel, project page on success
 * - `skipAll` - no redirect on cancel or success
 */
type RedirectBehavior = "default" | "skipCancel" | "skipAll";
interface NewProjectWizardProps {
    spaceId: string;
    projectSlug?: string;
    projectGroupId?: string;
    open: boolean;
    saveProjectOptions?: boolean;
    close: (project: ProjectResource | undefined) => void;
    back?: () => void;
    doBusyTask: DoBusyTask;
    isOnboarding?: boolean;
    redirectBehavior?: RedirectBehavior;
    projectStatus: ProjectStatus;
    resumingPage?: PageType;
    onResumedPage?: () => void;
}
interface NewProjectWizardState {
    project: ProjectResource;
    projectOption: ProjectCreatedOption;
    environments?: Array<EnvironmentResource>;
    targets?: Array<DeploymentTargetResource>;
    argoCDInstancesCount: number;
    projectTemplates?: Array<PublishedAndSharedProjectTemplateSummary>;
    templateFormData?: CreateProjectInput;
    templateFormOption?: ProjectCreatedOption;
    selectedProjectTemplate?: PublishedAndSharedProjectTemplateSummary;
    selectedProjectTemplateDetails?: ProjectTemplateDetailsResource;
}
type PageType = "ProjectTemplateSelection" | "Project" | "TemplateVersionSelection" | "Environments" | "KubernetesCluster" | "ProjectDetailsPage" | "ArgoCDInstance";
const defaultProjectOptions: ProjectCreatedOption = {
    projectId: undefined,
    vcsRedirect: false,
    newlyCreatedProjectRedirect: true,
    infrastructure: undefined,
    infrastructureSubclass: undefined,
    step: undefined,
};
function NewProjectWizard({ isOnboarding = true, redirectBehavior = "default", close, ...props }: NewProjectWizardProps) {
    const hideBackButton = redirectBehavior === "skipAll";
    const [currentPage, setCurrentPage] = useState<PageType | undefined>(isOnboarding ? "Project" : undefined);
    const [initialPage, setInitialPage] = useState<PageType | undefined>(isOnboarding ? "Project" : undefined);
    const [state, setState] = useState<NewProjectWizardState>();
    const navigate = useSpaceAwareNavigation();
    const [onboardingProjectOption, setOnboardingProjectOption] = useLocalStorage<ProjectCreatedOption>(`onboardingDialog.${props.spaceId}.ProjectOptions`, defaultProjectOptions);
    const [initialTargets, setInitialTargets] = useState<Array<DeploymentTargetResource>>([]);
    const [initialArgoCDInstancesCount, setInitialArgoCDInstancesCount] = useState<number>(0);
    const [spaceHasProjects, setSpaceHasProjects] = useState<boolean>(false);
    const correlationId = useCorrelationId();
    const trackAction = useAnalyticTrackedActionDispatch();
    const dispatchAction = useAnalyticActionDispatch();
    const guidedSetupDispatchAction = useAnalyticGuidedSetupDispatch();
    const isProjectTemplatesFeatureEnabled = useOctopusFeatureToggle("project-templates", false);
    useDoBusyTaskEffect(props.doBusyTask, async () => {
        const project = props.projectSlug ? await repository.Projects.get(props.projectSlug!) : undefined;
        const environments = await repository.Environments.all();
        const projectsInSpace = await repository.UserOnboarding.getProjectsOnboardingForCurrentUser();
        setSpaceHasProjects(!!projectsInSpace.TotalProjectsInSpace);
        const kubernetesTargets = isAllowed({ permission: Permission.MachineView, wildcard: true }) ? (await repository.Machines.list({ deploymentTargetTypes: "KubernetesTentacle,Kubernetes" })).Items : [];
        setInitialTargets(kubernetesTargets);
        const argoCDInstancesCount = isAllowed({ permission: Permission.MachineView, wildcard: true }) ? (await repository.ArgoCDInstances.list()).TotalCount : 0;
        setInitialArgoCDInstancesCount(argoCDInstancesCount);
        const getProjectTemplates = async () => {
            if (!isProjectTemplatesFeatureEnabled) {
                return [];
            }
            try {
                // This should only be called for instances with project templates enabled and existing project templates.
                // In the long run we will likely need a better check here rather than swallowing a failing API request.
                // For now we just swallow this exception incase project templates are mistakenly enabled.
                const response = await repository.TemplatedProjects.getPublishedAndSharedTemplates({
                    partialName: "",
                });
                return response.ProjectTemplates;
            }
            catch (error) {
                return [];
            }
        };
        const projectTemplates = await getProjectTemplates();
        projectTemplates.sort((a: PublishedAndSharedProjectTemplateSummary, b: PublishedAndSharedProjectTemplateSummary) => a.Name.localeCompare(b.Name));
        const projectOption = isOnboarding && onboardingProjectOption.projectId === project?.Id ? onboardingProjectOption : defaultProjectOptions;
        if (props.projectSlug && project) {
            setState({ project, projectOption, environments, argoCDInstancesCount, projectTemplates });
            const initialPage = getNextPage(project, projectOption, environments, kubernetesTargets, argoCDInstancesCount, projectTemplates);
            setInitialPage(initialPage);
        }
        else {
            setState((prev) => ({ ...prev!, environments, argoCDInstancesCount, projectTemplates }));
        }
        nextPage(project, projectOption, environments, kubernetesTargets, argoCDInstancesCount, projectTemplates);
    }, []);
    const nextPage = (project: ProjectResource | undefined, projectOptions: ProjectCreatedOption | undefined, environments: Array<EnvironmentResource> | undefined, targets: DeploymentTargetResource[] | undefined, argoCDInstancesCount: number | undefined, projectTemplates: PublishedAndSharedProjectTemplateSummary[] | undefined, templateFormData?: CreateProjectInput) => {
        const nextPage = getNextPage(project, projectOptions, environments, targets, argoCDInstancesCount || 0, projectTemplates, templateFormData);
        setCurrentPage(nextPage);
    };
    const previousPage = () => {
        props.back?.();
    };
    const closeAndRedirect = (toProject: boolean = true) => {
        props.projectStatus.dispatch({ type: projectStatusActions.guidedSetupDismissed });
        if (redirectBehavior !== "default") {
            close(state?.project);
        }
        else if (!toProject) {
            navigate.navigate(links.projectsPage.generateUrl({ spaceId: props.spaceId }));
            close(state?.project);
        }
        else {
            redirectToProject();
        }
    };
    const projectTemplatesSkipped = () => {
        setState((prev) => ({ ...prev!, selectedProjectTemplate: undefined, selectedProjectTemplateDetails: undefined }));
        nextPage(state?.project, state?.projectOption, state?.environments, state?.targets, state?.argoCDInstancesCount || 0, state?.projectTemplates);
    };
    const onTemplateSelected = (template: PublishedAndSharedProjectTemplateSummary) => {
        setState((prev) => ({ ...prev!, selectedProjectTemplate: template }));
        props.projectStatus?.dispatch?.({ type: projectStatusActions.projectTemplateSelected });
        nextPage(state?.project, state?.projectOption, state?.environments, state?.targets, state?.argoCDInstancesCount, state?.projectTemplates);
    };
    const onTemplateDetailsSelected = (templateDetails: ProjectTemplateDetailsResource) => {
        setState((prev) => ({ ...prev!, selectedProjectTemplateDetails: templateDetails }));
    };
    const getTemplateVersionSummary = () => {
        if (!state?.selectedProjectTemplate) {
            return null;
        }
        const template = state.selectedProjectTemplate;
        return {
            latestStableVersion: template.LatestVersion?.Version ?? null,
            latestPreReleaseVersion: template.LatestPreReleaseVersion?.Version ?? null,
        };
    };
    const getProjectTemplateConfig = (): ProjectTemplateConfig | undefined => {
        const selectedTemplate = state?.selectedProjectTemplate;
        const versionSummary = getTemplateVersionSummary();
        if (!selectedTemplate || !versionSummary) {
            return undefined;
        }
        return {
            selectedTemplate,
            versionSummary,
            onDetailsSelected: onTemplateDetailsSelected,
            templateDetails: state?.selectedProjectTemplateDetails,
        };
    };
    const handleProceedToVersionSelection = (formData: CreateProjectInput, option: ProjectCreatedOption) => {
        setState((prev) => ({ ...prev!, templateFormData: formData, templateFormOption: option }));
        nextPage(undefined, option, state?.environments, state?.targets?.length ? state?.targets : initialTargets, state?.argoCDInstancesCount || 0, state?.projectTemplates, formData);
    };
    const createProjectWithTemplate = async (templateDetails: ProjectTemplateDetailsResource): Promise<boolean> => {
        const formData = state?.templateFormData;
        const option = state?.templateFormOption;
        if (!formData || !option)
            return false;
        setState((prev) => ({ ...prev!, templateFormData: undefined, templateFormOption: undefined }));
        return await props.doBusyTask(async () => {
            return await trackAction("Save Project", { action: Action.Save, resource: "Project", isCaCenabled: formData.vcsRedirect ? "True" : "False" }, async () => {
                const result = await createProject(formData, option, templateDetails);
                projectCreated(result, { ...option, projectId: result.Id });
                return true;
            });
        });
    };
    const projectCreated = (project: ProjectResource, option: ProjectCreatedOption) => {
        guidedSetupDispatchAction("Submit New Project Setup Screen", {
            projectId: project.Id,
            correlationId: correlationId,
            projectType: option.infrastructure,
            projectSubType: option.step,
            versionControl: option.vcsRedirect,
            sourcePage: "Project Setup",
        });
        setState((prev) => ({ ...prev!, project, projectOption: option, templateFormData: undefined, templateFormOption: undefined }));
        if (props.saveProjectOptions) {
            setOnboardingProjectOption(option);
        }
        nextPage(project, option, state?.environments, state?.targets?.length ? state?.targets : initialTargets, state?.argoCDInstancesCount || 0, state?.projectTemplates);
    };
    const environmentsCreated = async () => {
        const environments = await repository.Environments.all();
        guidedSetupDispatchAction("Submit Environments Setup Screen", { projectId: state?.project.Id, correlationId: correlationId, environments: environments.map((x) => x.Name).join(";"), sourcePage: "Project Setup" });
        setState((prev) => ({ ...prev!, environments }));
        props.projectStatus.dispatch({ type: projectStatusActions.environmentsAdded });
        nextPage(state?.project, state?.projectOption, environments, state?.targets?.length ? state?.targets : initialTargets, state?.argoCDInstancesCount, undefined);
    };
    const environmentsSkipped = async () => {
        guidedSetupDispatchAction("Exit Project Setup", { projectId: state?.project.Id, correlationId, screen: "Environments", sourcePage: "Project Setup" });
        props.projectStatus.dispatch({ type: projectStatusActions.environmentsSkipped });
        nextPage(state?.project, state?.projectOption, undefined, undefined, undefined, undefined);
    };
    const clusterConnected = () => {
        props.projectStatus.dispatch({ type: projectStatusActions.k8sClusterAdded });
        nextPage(state?.project, state?.projectOption, state?.environments, state?.targets, undefined, undefined);
    };
    const clusterSkipped = () => {
        guidedSetupDispatchAction("Exit Project Setup", { projectId: state?.project.Id, correlationId, screen: "Kubernetes Cluster", sourcePage: "Project Setup" });
        props.projectStatus.dispatch({ type: projectStatusActions.k8sClusterSkipped });
        nextPage(state?.project, state?.projectOption, state?.environments, undefined, undefined, undefined);
    };
    const argoCDInstanceConnected = () => {
        props.projectStatus.dispatch({ type: projectStatusActions.argoCDInstanceAdded });
        nextPage(state?.project, state?.projectOption, state?.environments, undefined, state?.argoCDInstancesCount, undefined);
    };
    const argoCDInstanceSkipped = () => {
        guidedSetupDispatchAction("Exit Project Setup", { projectId: state?.project.Id, correlationId, screen: "Argo CD Instance", sourcePage: "Project Setup" });
        props.projectStatus.dispatch({ type: projectStatusActions.argoCDInstanceSkipped });
        nextPage(state?.project, state?.projectOption, state?.environments, undefined, undefined, undefined);
    };
    const getProjectURL = useCallback((): Url | undefined => {
        if (!state) {
            return;
        }
        if (props.projectSlug && initialPage === "ProjectDetailsPage") {
            return links.deploymentProcessPage.generateUrl({ spaceId: state.project.SpaceId, projectSlug: state.project.Slug }, { newlyCreatedProject: false });
        }
        if (props.projectSlug && initialPage !== "ProjectDetailsPage") {
            return links.deploymentProcessEditorPage.generateUrl({ spaceId: state.project.SpaceId, projectSlug: state.project.Slug }, { stepTemplates: true, newlyCreatedProject: !spaceHasProjects, actionCategory: ActionHandlerCategory.Featured });
        }
        if (state.projectOption.vcsRedirect) {
            return links.projectVersionControlSettingsPage.generateUrl({ spaceId: state.project.SpaceId, projectSlug: state.project.Slug }, { newlyCreatedProject: !spaceHasProjects });
        }
        if (state.project.ProjectTemplateDetails) {
            return links.deploymentProcessPage.generateUrl({ spaceId: state.project.SpaceId, projectSlug: state.project.Slug }, { newlyCreatedProject: !spaceHasProjects });
        }
        if (!state.projectOption.infrastructure) {
            return links.deploymentProcessEditorPage.generateUrl({ spaceId: state.project.SpaceId, projectSlug: state.project.Slug }, { stepTemplates: true, newlyCreatedProject: !spaceHasProjects, actionCategory: ActionHandlerCategory.Featured });
        }
        if (state.projectOption.infrastructure && !state.projectOption.step) {
            const stepCategory = getStepCategory(state.projectOption.infrastructure, state.projectOption.infrastructureSubclass);
            return links.deploymentProcessEditorPage.generateUrl({ spaceId: state.project.SpaceId, projectSlug: state.project.Slug }, { stepTemplates: true, newlyCreatedProject: !spaceHasProjects, actionCategory: stepCategory });
        }
        const targetTags = state?.targets?.flatMap((target) => target.Roles);
        return links.deploymentProcessEditorPage.generateUrl({ spaceId: state.project.SpaceId, projectSlug: state.project.Slug }, { new: true, actionType: state.projectOption.step, parentStepId: undefined, templateId: undefined, newlyCreatedProject: !spaceHasProjects, tags: targetTags });
    }, [initialPage, props.projectSlug, spaceHasProjects, state]);
    const redirectToProject = useCallback(() => {
        const redirectUrl = getProjectURL();
        if (redirectUrl) {
            guidedSetupDispatchAction("Project Setup Completed", { projectId: state?.project.Id, correlationId: correlationId, sourcePage: "Project Setup" });
            close(state?.project);
            navigate.navigate(redirectUrl!);
        }
        close(state?.project);
    }, [correlationId, getProjectURL, guidedSetupDispatchAction, navigate, close, state?.project]);
    const getStepCategory = (projectInfrastructure: ProjectInfrastructure, infrastructureSubClass: ProjectInfrastructureSubclass | undefined) => {
        switch (projectInfrastructure) {
            case ProjectInfrastructure.AWS:
                return ActionHandlerCategory.Aws;
            case ProjectInfrastructure.Azure:
                return ActionHandlerCategory.Azure;
            case ProjectInfrastructure.Windows:
                return ActionHandlerCategory.WindowsServer;
            case ProjectInfrastructure.Kubernetes: {
                switch (infrastructureSubClass) {
                    case ProjectInfrastructureSubclass.KubernetesArgoCD:
                        return ActionHandlerCategory.ArgoCD;
                    case ProjectInfrastructureSubclass.KubernetesOther:
                        return ActionHandlerCategory.Kubernetes;
                    default:
                        return ActionHandlerCategory.Featured;
                }
            }
            default:
                return ActionHandlerCategory.Featured;
        }
    };
    const getNextPage = (project: ProjectResource | undefined, projectOptions: ProjectCreatedOption | undefined, environments: Array<EnvironmentResource> | undefined, targets: DeploymentTargetResource[] | undefined, argoCDInstancesCount: number, projectTemplates: PublishedAndSharedProjectTemplateSummary[] | undefined, templateFormData?: CreateProjectInput): PageType => {
        const hasTemplateFormData = templateFormData ?? state?.templateFormData;
        if (isProjectTemplatesFeatureEnabled && !project && !currentPage && projectTemplates && projectTemplates.length > 0) {
            return "ProjectTemplateSelection";
        }
        else if (!project && !hasTemplateFormData) {
            return "Project";
        }
        if ((project || hasTemplateFormData) && state?.selectedProjectTemplate && !state?.selectedProjectTemplateDetails && currentPage !== "TemplateVersionSelection") {
            return "TemplateVersionSelection";
        }
        if (!project) {
            return "Project";
        }
        else if (!environments?.length && currentPage !== "Environments") {
            return "Environments";
        }
        else if (environments?.length && projectOptions?.step && currentPage !== "KubernetesCluster" && !targets?.length && isAllowed({ permission: Permission.MachineView, wildcard: true })) {
            return "KubernetesCluster";
        }
        else if (environments?.length && projectOptions?.infrastructureSubclass === "KubernetesArgoCD" && currentPage !== "ArgoCDInstance" && argoCDInstancesCount === 0 && isAllowed({ permission: Permission.MachineView, wildcard: true })) {
            return "ArgoCDInstance";
        }
        else {
            return "ProjectDetailsPage";
        }
    };
    const projectName = state?.templateFormData?.name ?? state?.project?.Name ?? "";
    const projectLogoUrl = state?.project?.Links.Logo;
    const isKubernetesFlow = state?.projectOption?.infrastructure === ProjectInfrastructure.Kubernetes;
    const pages = new Map<PageType, JSX.Element>();
    useEffect(() => {
        if (props.open && currentPage === "ProjectDetailsPage" && !props.resumingPage) {
            redirectBehavior === "skipAll" ? close(state?.project) : redirectToProject();
        }
    }, [close, currentPage, props.open, props.resumingPage, redirectBehavior, redirectToProject, state?.project]);
    if (!props.open)
        return null;
    if (props.resumingPage !== undefined) {
        props.onResumedPage?.();
        if (currentPage !== props.resumingPage) {
            setCurrentPage(props.resumingPage);
        }
    }
    if (isProjectTemplatesFeatureEnabled) {
        pages.set("ProjectTemplateSelection", <ProjectTemplateSelectionPage title={<></>} projectTemplates={state?.projectTemplates ?? []} isOnboarding={isOnboarding} onTemplateSkipped={projectTemplatesSkipped} onTemplateSelected={onTemplateSelected} close={() => closeAndRedirect(false)} spaceHasEnvironments={(state?.environments?.length ?? 0) > 0} doBusyTask={props.doBusyTask}/>);
    }
    pages.set("Project", <CreateNewProject next={projectCreated} onProceedToVersionSelection={handleProceedToVersionSelection} doBusyTask={props.doBusyTask} back={previousPage} close={() => closeAndRedirect(false)} spaceId={props.spaceId} projectGroupId={props.projectGroupId} isOnboarding={isOnboarding} k8sTargetAlreadyExistsInSpace={initialTargets.length > 0} argoCDInstanceAlreadyExistsInSpace={initialArgoCDInstancesCount > 0} projectStatus={props.projectStatus} spaceHasEnvironments={(state?.environments?.length ?? 0) > 0} hasProjectTemplates={(state?.projectTemplates?.length ?? 0) > 0} projectTemplateConfig={getProjectTemplateConfig()}/>);
    if (state?.selectedProjectTemplate) {
        const versionSummary = getTemplateVersionSummary();
        if (versionSummary) {
            pages.set("TemplateVersionSelection", <ProjectTemplateVersionSelectionPage selectedTemplate={state.selectedProjectTemplate} versionSummary={versionSummary} onVersionSelected={onTemplateDetailsSelected} createProject={createProjectWithTemplate} back={() => {
                    setState((prev) => ({ ...prev!, selectedProjectTemplateDetails: undefined }));
                    setCurrentPage("Project");
                }} close={() => closeAndRedirect(false)} isOnboarding={isOnboarding} hideBackButton={hideBackButton} title={<NewProjectWizardTitle projectName={projectName} projectLogoUrl={projectLogoUrl} spaceId={props.spaceId} clickOnProjectsLink={() => close(state?.project)} isOnboarding={isOnboarding}>
                            <ProjectStatusBar state={props.projectStatus.state} dispatch={props.projectStatus.dispatch} projectSlug={state?.project?.Slug} spaceId={props.spaceId} hideCloseButton/>
                        </NewProjectWizardTitle>} nextButtonTitle={isOnboarding ? "Next" : "Create Project"}/>);
        }
    }
    pages.set("Environments", <CreateEnvironmentsPage isKubernetesFlow={isKubernetesFlow} spaceId={props.spaceId} hideBackButton={hideBackButton} title={<NewProjectWizardTitle projectName={projectName} projectLogoUrl={projectLogoUrl} spaceId={props.spaceId} clickOnProjectsLink={() => close(state?.project)} isOnboarding={isOnboarding}>
                    <ProjectStatusBar state={props.projectStatus.state} dispatch={props.projectStatus.dispatch} projectSlug={state?.project?.Slug} spaceId={props.spaceId} hideCloseButton/>
                </NewProjectWizardTitle>} nextButtonTitle={isOnboarding ? "Next" : "Create Environments"} next={async () => await environmentsCreated()} skipButtonTitle="I'll do this later" isOnboarding={isOnboarding} skip={environmentsSkipped} close={closeAndRedirect} trackAction={trackAction} dispatchAction={dispatchAction}/>);
    pages.set("KubernetesCluster", <CreateNewKubernetesCluster spaceId={props.spaceId} projectId={state?.project?.Id} correlationId={correlationId} next={clusterConnected} close={closeAndRedirect} hideBackButton={hideBackButton} title={<NewProjectWizardTitle projectName={projectName} projectLogoUrl={projectLogoUrl} spaceId={props.spaceId} clickOnProjectsLink={() => close(state?.project)} isOnboarding={isOnboarding}>
                    <ProjectStatusBar state={props.projectStatus.state} dispatch={props.projectStatus.dispatch} projectSlug={state?.project?.Slug} spaceId={props.spaceId} hideCloseButton/>
                </NewProjectWizardTitle>} environments={state?.environments} isOnboarding={isOnboarding} skip={clusterSkipped} onTargetsChange={(targets) => setState((prev) => ({ ...prev!, targets }))} projectStatus={props.projectStatus}/>);
    pages.set("ArgoCDInstance", <ConnectArgoCDInstance spaceId={props.spaceId} projectId={state?.project?.Id} correlationId={correlationId} next={argoCDInstanceConnected} skip={argoCDInstanceSkipped} close={closeAndRedirect} hideBackButton={hideBackButton} title={<NewProjectWizardTitle projectName={projectName} projectLogoUrl={projectLogoUrl} spaceId={props.spaceId} clickOnProjectsLink={() => close(state?.project)} isOnboarding={isOnboarding}>
                    <ProjectStatusBar state={props.projectStatus.state} dispatch={props.projectStatus.dispatch} projectSlug={state?.project?.Slug} spaceId={props.spaceId} hideCloseButton/>
                </NewProjectWizardTitle>} environments={state?.environments} isOnboarding={isOnboarding} onArgoCDInstancesAdded={(addedInstances) => setState((prev) => ({ ...prev!, argoCDInstancesCount: addedInstances.length }))} projectStatus={props.projectStatus}/>);
    if (!currentPage || !pages.has(currentPage))
        return null;
    return pages.get(currentPage)!;
}
interface NewProjectWizardTitleProps {
    projectName: string;
    projectLogoUrl: string | undefined;
    spaceId: string;
    isOnboarding: boolean;
    clickOnProjectsLink: () => void;
    children: React.ReactNode;
}
function NewProjectWizardTitle(props: NewProjectWizardTitleProps) {
    return (<div>
            {!props.isOnboarding && (<InternalLink className={newProjectWizardTitleStyles.projectsLink} to={links.projectsPage.generateUrl({ spaceId: props.spaceId })} onClick={props.clickOnProjectsLink}>
                    <ArrowLeftIcon size={20}/>
                    <span>Projects</span>
                </InternalLink>)}
            <div className={newProjectWizardTitleStyles.projectContainer}>
                {props.projectLogoUrl && <img className="projectLogo" src={props.projectLogoUrl} alt="Project logo"/>}
                {props.projectName}
            </div>
            <div>{props.children}</div>
        </div>);
}
const newProjectWizardTitleStyles = {
    projectsLink: css({
        paddingBottom: space[8],
        display: "flex",
        font: text.body.regular.small,
        gap: space[4],
    }),
    projectContainer: css({
        display: "flex",
        alignItems: "center",
        marginBottom: space[16],
        marginTop: space[8],
        textOverflow: "ellipsis",
        color: themeTokens.color.text.primary,
        fontSize: fontSize["base"],
        fontWeight: fontWeight["700"],
        lineHeight: "1.5rem",
        "& .projectLogo": {
            width: "24px",
            height: "24px",
            borderRadius: borderRadius.small,
            backgroundColor: themeTokens.color.background.secondary.default,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginRight: space[8],
            "& svg": {
                width: "18px",
                height: "18px",
            },
            "& svg path": {
                fill: themeTokens.color.icon.primary,
            },
        },
    }),
};
export default NewProjectWizard;
